
import api from './api';

// jsPDF (~200 KB) is loaded on demand inside exportDeckToPDF so it stays out of
// the initial bundle — PDF export is a rare action.

// Helper function to search cards using our backend API
async function searchCards(query, pageSize = 10) {
  try {
    const { data } = await api.get('/api/cards/search', {
      params: { q: query, pageSize }
    });
    return data?.cards || [];
  } catch (error) {
    console.error('Card search error:', error);
    return [];
  }
}

// Wrapper function to match the old pokemon.card.where API
async function searchCardsAPI(params) {
  const { q, page = 1, pageSize = 10 } = params;
  const cards = await searchCards(q, pageSize);
  return { data: cards };
}

/**
 * Match Export Utilities (CSV)
 */
const flattenMatchData = (match) => {
  const matchInfo = {
    id: match._id,
    date: new Date(match.createdAt).toISOString().split('T')[0],
    deckName: match.activeDeck?.name || '',
    opponentDeck: match.opponentDeck,
    opponentDeckVariant: match.opponentDeckVariant || '',
    bestOf: match.bestOf,
    format: match.format || '',
    importedFrom: match.importedFrom || '',
    tags: Array.isArray(match.tags) ? match.tags.join(', ') : (match.tags || ''),
    notes: match.notes || '',
    wonCoinFlip: match.bestOf > 1 ? (match.wonCoinFlip ? 'Yes' : 'No') : 'N/A'
  };

  const wins = match.games.filter(game => game.result === 'win').length;
  const losses = match.games.filter(game => game.result === 'loss').length;
  const neededWins = Math.ceil(match.bestOf / 2);

  matchInfo.matchResult = wins >= neededWins ? 'Win' : losses >= neededWins ? 'Loss' : 'In Progress';
  matchInfo.gameScore = `${wins}-${losses}`;

  const firstTurnGames = match.games.filter(game => game.wentFirst === true).length;
  const firstTurnWins = match.games.filter(game => game.wentFirst === true && game.result === 'win').length;
  const secondTurnGames = match.games.filter(game => game.wentFirst === false).length;
  const secondTurnWins = match.games.filter(game => game.wentFirst === false && game.result === 'win').length;

  matchInfo.firstTurnGames = firstTurnGames;
  matchInfo.firstTurnWins = firstTurnWins;
  matchInfo.secondTurnGames = secondTurnGames;
  matchInfo.secondTurnWins = secondTurnWins;

  for (let i = 0; i < 5; i++) {
    if (i < match.games.length) {
      matchInfo[`game${i+1}Result`] = match.games[i].result === 'win' ? 'Win' : 'Loss';
      matchInfo[`game${i+1}Turn`] = match.games[i].wentFirst ? 'First' : 'Second';
    } else {
      matchInfo[`game${i+1}Result`] = 'N/A';
      matchInfo[`game${i+1}Turn`] = 'N/A';
    }
  }

  return matchInfo;
};

const flattenVGCMatchData = (match) => {
  const matchInfo = {
    id: match._id,
    date: match.date
      ? new Date(match.date).toISOString().split('T')[0]
      : (match.createdAt ? new Date(match.createdAt).toISOString().split('T')[0] : ''),
    result: match.result || '',
    format: match.format || '',
    bestOf: match.bestOfFormat || '',
    opponentName: match.opponentName || '',
    playerTeam: Array.isArray(match.playerTeamArray) ? match.playerTeamArray.join(', ') : '',
    opponentTeam: Array.isArray(match.opponentTeamArray) ? match.opponentTeamArray.join(', ') : '',
    leadPokemon: Array.isArray(match.leadPokemon) ? match.leadPokemon.join(', ') : '',
    opponentLeadPokemon: Array.isArray(match.opponentLeadPokemon) ? match.opponentLeadPokemon.join(', ') : '',
    broughtPokemon: Array.isArray(match.broughtPokemon) ? match.broughtPokemon.join(', ') : '',
    opponentBroughtPokemon: Array.isArray(match.opponentBroughtPokemon) ? match.opponentBroughtPokemon.join(', ') : '',
    replayLink: match.replayLink || '',
    tags: Array.isArray(match.tags) ? match.tags.join(', ') : (match.tags || ''),
    isPublic: match.isPublic ? 'Yes' : 'No'
  };

  const games = Array.isArray(match.games) ? match.games : [];
  for (let i = 0; i < 5; i++) {
    if (i < games.length) {
      matchInfo[`game${i+1}Result`] = games[i].result || 'N/A';
      matchInfo[`game${i+1}Notes`] = games[i].notes || '';
    } else {
      matchInfo[`game${i+1}Result`] = 'N/A';
      matchInfo[`game${i+1}Notes`] = '';
    }
  }

  return matchInfo;
};

const convertToCSV = (objArray) => {
  if (objArray.length === 0) return '';
  const header = Object.keys(objArray[0]).join(',');
  const rows = objArray.map(obj =>
    Object.values(obj).map(value =>
      typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value
    ).join(',')
  );
  return [header, ...rows].join('\n');
};

export const exportToCSV = (matches, filename, type = 'tcg') => {
  const flattener = type === 'vgc' ? flattenVGCMatchData : flattenMatchData;
  const flattenedData = matches.map(flattener);
  const csvContent = convertToCSV(flattenedData);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Deck Export (Clipboard/Text/PDF)
 */
export const formatDeckForClipboard = (deck) => {
  if (!deck) return '';
  const { name = '', pokemon = [], trainers = [], energy = [] } = deck;

  const formatSection = (title, cards) => {
    const total = cards.reduce((sum, c) => sum + (c.quantity || c.count || 1), 0);
    let section = `${title}: ${total}\n`;
    const sorted = [...cards].sort((a, b) => a.name.localeCompare(b.name));
    for (const card of sorted) {
      const qty = card.quantity || card.count || 1;
      
      // Get set code - prioritize ptcgoCode for PTCGL compatibility
      let setCode = '';
      if (card.set?.ptcgoCode) {
        setCode = card.set.ptcgoCode.toUpperCase();
      } else if (card.resolvedSetCode) {
        setCode = card.resolvedSetCode;
      } else if (card.set?.id) {
        setCode = card.set.id.toUpperCase();
      } else if (card.id && card.id.includes('-')) {
        // Extract set code from card ID as a last resort
        setCode = card.id.split('-')[0].toUpperCase();
      } else {
        setCode = 'SVI'; // Default fallback code
      }
      
      // Ensure proper card number format
      const cardNumber =
        card.number ||
        card.cardNumber || // support alternate field just in case
        (card.id?.includes('-') ? card.id.split('-')[1].toUpperCase() : '1') ||
        '1';
      
      // Format all cards consistently with the expected import format
      section += `${qty} ${card.name} ${setCode} ${cardNumber}\n`;
    }
    return section;
  };

  // Format the output with the exact section names expected by import
  let output = `${name}\n\n`;
  output += formatSection('Pokémon', pokemon);
  output += `\n${formatSection('Trainer', trainers)}`;
  output += `\n${formatSection('Energy', energy)}`;
  return output.trim();
};

export const exportDeckToClipboard = (deck) => {
  const text = formatDeckForClipboard(deck);
  navigator.clipboard.writeText(text)
    .then(() => alert('Deck list copied to clipboard!'))
    .catch((err) => {
      console.error('Clipboard export failed:', err);
      alert('Failed to copy deck list to clipboard.');
    });
};

export const exportDeckToTextFile = (deck) => {
  // Use the same formatting function as clipboard export
  const text = formatDeckForClipboard(deck);
  const filename = `${deck.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_decklist.txt`;
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportDeckToPDF = async (deck) => {
  if (!deck) return;
  const { name } = deck;
  // Use the same formatting function as clipboard export
  const pdfText = formatDeckForClipboard(deck);
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  const titleWidth = doc.getStringUnitWidth(name) * 18 / doc.internal.scaleFactor;
  const titleX = (210 - titleWidth) / 2;
  doc.text(name, titleX, 20);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const lines = pdfText.split('\n');
  let y = 30;
  for (const line of lines) {
    if (y > 280) {
      doc.addPage();
      y = 20;
    }
    doc.text(line, 20, y);
    y += 6;
  }
  doc.save(`${name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_decklist.pdf`);
};

/**
 * Deck Import
 */
export const importDeckFromClipboard = async (clipboardText = null) => {
  if (!clipboardText) {
    clipboardText = await navigator.clipboard.readText();
  }

  if (!clipboardText.trim()) {
    throw new Error('Clipboard is empty. Please copy a deck list first.');
  }

  const pokemonCards = [], trainerCards = [], energyCards = [];
  const failedImports = [];

  const lines = clipboardText
    .replace(/\r\n|\r|\n/g, '\n')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

  // Auto-detect if the deck list has section headers
  let hasSections = false;
  for (const line of lines) {
    const lower = line.toLowerCase();
    if (lower.startsWith('pokémon') || lower.startsWith('pokemon') || 
        lower.startsWith('trainer') || lower.startsWith('energy')) {
      hasSections = true;
      break;
    }
  }

  // If no section headers, assume the first part is all Pokemon cards
  let section = hasSections ? null : 'pokemon';
  let deckName = "Imported Deck";
if (lines.length > 0) {
  deckName = lines[0];
  const secondLine = lines[1]?.toLowerCase?.() || '';
  if (/^pok[eé]mon[:]?/i.test(secondLine) || /^trainer[:]?/i.test(secondLine) || /^energy[:]?/i.test(secondLine)) {
    // Skip the second line in parsing, but keep deckName as lines[0]
    lines.splice(1, 1); // remove the section header so it can be processed again normally later
  } else if (/^pok[eé]mon[:]?/i.test(deckName.toLowerCase()) || /^trainer[:]?/i.test(deckName.toLowerCase()) || /^energy[:]?/i.test(deckName.toLowerCase())) {
    // First line is not a name, it's a section, use default title instead
    deckName = "Imported Deck";
  }
}

  console.log("Initial section state:", section, "hasSections:", hasSections);

  for (const line of lines) {
    const lower = line.toLowerCase();
    
    // Skip the first line (deck name)
    if (lines.indexOf(line) === 0 && line === deckName) continue; // skip actual deck title
    
    if (/^pok[eé]mon/i.test(lower)) {
  section = 'pokemon';
  console.log("Set section to pokemon");
  continue;
}
    if (lower.startsWith('trainer')) { section = 'trainers'; console.log("Set section to trainers"); continue; }
    if (lower.startsWith('energy')) { section = 'energy'; console.log("Set section to energy"); continue; }
    if (!section || /^total/i.test(line)) continue;

    // Check for energy card format like "7 Basic {D} Energy Energy 15 PH"
    // Also handle formats like "7 Darkness Energy" or "4 {L} Energy"
    const energyMatch = line.match(/^(\d+)\s+(?:Basic\s+)?(?:\{([A-Z])\})?\s*(.+)?\s*Energy/i);
    if (section === 'energy' && energyMatch) {
      const [, quantityStr, typeLetter, energyTypePart] = energyMatch;
      const quantity = parseInt(quantityStr, 10);
      
      // Energy type mapping for both {D} format and explicit names
      const energyMap = {
        D: 'Darkness',
        F: 'Fighting',
        G: 'Grass',
        L: 'Lightning',
        M: 'Metal',
        P: 'Psychic',
        R: 'Fire',
        W: 'Water',
        C: 'Colorless',
        // Also map energy names directly
        DARKNESS: 'Darkness',
        FIGHTING: 'Fighting',
        GRASS: 'Grass',
        LIGHTNING: 'Lightning',
        METAL: 'Metal',
        PSYCHIC: 'Psychic',
        FIRE: 'Fire',
        WATER: 'Water',
        COLORLESS: 'Colorless'
      };

      // First, try to find the card by exact name (for special energy cards like "Reversal Energy")
      if (energyTypePart && energyTypePart.trim()) {
        const fullCardName = `${energyTypePart.trim()} Energy`;
        try {
          const exactCards = await searchCards(`name:"${fullCardName}" supertype:energy`, 2);
          const exactResults = { data: exactCards };

          if (exactResults?.data?.length > 0) {
            const energyCard = exactResults.data[0];
            energyCards.push({
              ...energyCard,
              quantity,
              count: quantity,
              imageUrl: energyCard.images?.small || energyCard.imageUrl,
              images: energyCard.images,
              _timestamp: Date.now() + Math.random()
            });
            continue;
          }
        } catch (e) {
          console.error('Exact energy search error:', e);
        }
      }

      // If exact match failed, try basic energy mapping
      let energyName;
      if (typeLetter) {
        energyName = energyMap[typeLetter];
      } else if (energyTypePart) {
        energyName = energyMap[energyTypePart.toUpperCase()];
      }

      if (energyName) {
        try {
          const fullEnergyName = `${energyName} Energy`;
          const energyResults = await searchCardsAPI({
            q: `name:"${fullEnergyName}" supertype:energy subtypes:basic`
          });

          let basicEnergy = energyResults?.data?.[0];
          
          // If no results found, try a more generic search for any basic energy
          if (!basicEnergy && energyResults?.data?.length === 0) {
            const fallbackResults = await searchCardsAPI({
              q: "supertype:energy subtypes:basic"
            });
            
            // Try to find an energy of the right type
            basicEnergy = fallbackResults?.data?.find(card => 
              card.name.toLowerCase().includes(energyName.toLowerCase())
            );
            
            // Last resort: just grab any basic energy
            if (!basicEnergy && fallbackResults?.data?.length > 0) {
              basicEnergy = fallbackResults.data[0];
            }
          }

          if (basicEnergy) {
            energyCards.push({
              ...basicEnergy,
              quantity,
              count: quantity,
              imageUrl: basicEnergy.images?.small || basicEnergy.imageUrl,
              images: basicEnergy.images,
              _timestamp: Date.now() + Math.random()
            });
            continue;
          } else {
            failedImports.push(`${line} (Energy card not found)`);
          }
        } catch (e) {
          console.error('Energy fallback error:', e);
          failedImports.push(`${line} (Energy import error)`);
        }
      } else {
        failedImports.push(`${line} (Unknown energy type)`);
      }
    }

    // Clean and normalize the line
    const cleanedLine = line
      .replace(/\{[A-Z]\}/g, '')               // remove {D}, {G}, etc.
      .replace(/\bEnergy\b/gi, '')             // remove duplicate "Energy"
      .replace(/\bPH\b|\bREV\b|\bHolo\b/gi, '') // remove trailing variants
      .replace(/\s+/g, ' ')                    // collapse whitespace
      .trim();

    // Try different regex patterns for different card formats
    let match = cleanedLine.match(/^(\d+)[xX]?\s+(.+?)\s+([A-Z0-9\-]{2,})\s+([A-Z0-9]+)(?:\s+[A-Z]+)?$/);
    
    // For "Boss's Orders PR-SW 251" format with hyphenated set code
    if (!match) {
      match = cleanedLine.match(/^(\d+)[xX]?\s+(.+?)\s+([A-Z0-9]+\-[A-Z0-9]+)\s+([A-Z0-9]+)(?:\s+[A-Z]+)?$/);
    }
    
    // For "Ancient Booster Capsule PAR 159" format with shorter set code
    if (!match) {
      match = cleanedLine.match(/^(\d+)[xX]?\s+(.+?)\s+([A-Z]{3})\s+([A-Z0-9]+)(?:\s+[A-Z]+)?$/);
    }
    
    // Very loose fallback pattern that will try to capture most formats
    if (!match) {
      match = cleanedLine.match(/^(\d+)[xX]?\s+(.+?)\s+([A-Z0-9\-]{1,})\s+([A-Z0-9]+)$/);
    }
    
    if (!match) {
      console.warn('Skipping unmatched line:', line);
      failedImports.push(`${line} (Format not recognized)`);
      continue;
    }

    const [, quantityStr, rawName, rawSetCodeInput, rawNumber] = match;
    const quantity = parseInt(quantityStr, 10);
    const name = rawName.trim();

    // No need for hardcoded mappings - the matching logic below checks both
    // card.set.ptcgoCode (e.g., "MEG") and card.set.id (e.g., "me1")
    // This automatically handles all PTCGL codes from en.json
    const setCode = rawSetCodeInput.toUpperCase();
    const cardNumber = rawNumber.trim();

    // Build query - special handling for problematic cards
    let query;
    
    // Special case for Boss's Orders PR-SW
    if (name === "Boss's Orders" && setCode === "PR-SW") {
      query = `name:"Boss's Orders"`;
    }
    // Special case for Ancient Booster Capsule PAR
    else if (name === "Ancient Booster Capsule" && setCode === "PAR") {
      query = `name:"Ancient Booster Capsule"`;
    }
    // Regular case - search by name only first to get more results
    else {
      query = `name:"${name}"`;
      // Don't include set code in the initial query as it might be too restrictive
      // We'll filter by set code in the matching logic instead
    }

    let results;
    try {
      console.log(`Searching for card: "${name}" with query: "${query}"`);
      results = await searchCardsAPI({ q: query });
      console.log(`Found ${results?.data?.length || 0} results for "${name}"`);
    } catch (err) {
      console.error('Error fetching card for line:', line, err);
      failedImports.push(`${line} (API error: ${err.message || "Unknown error"})`);
      continue;
    }

    // Match logic with enhanced flexibility for set codes
    let matchCard;
    
    // Special case for Boss's Orders PR-SW
    if (name === "Boss's Orders" && setCode === "PR-SW") {
      matchCard = results?.data?.find(card => 
        card.name === "Boss's Orders" && 
        (card.number === "251" || card.number?.endsWith("251"))
      );
    } 
    // Special case for Ancient Booster Capsule PAR
    else if (name === "Ancient Booster Capsule" && setCode === "PAR") {
      matchCard = results?.data?.find(card => 
        card.name === "Ancient Booster Capsule" && 
        (card.number === "159" || card.number?.endsWith("159") || card.number?.includes("159"))
      );
    }
    // Regular matching logic
    else {
      matchCard = results?.data?.find(card => {
        // Check multiple set code fields for better matching
        const cardSetPtcgoCode = card.set?.ptcgoCode?.toUpperCase() || '';
        const cardSetId = card.set?.id?.toUpperCase() || '';
        const cardSetName = card.set?.name?.toUpperCase() || '';
        
        // Normalize input set code
        const inputSetCode = setCode.toUpperCase();
        const inputSetCodeNoHyphen = setCode.replace(/[^A-Z0-9]/gi, '').toUpperCase();
        
        // Check if any of the card's set identifiers match the input
        const setMatches = 
          cardSetPtcgoCode === inputSetCode ||
          cardSetId === inputSetCode ||
          cardSetPtcgoCode === inputSetCodeNoHyphen ||
          cardSetId === inputSetCodeNoHyphen ||
          // Check if input code is contained in set name (e.g., PAF in "Paldean Fates")
          (inputSetCode.length >= 3 && cardSetName.includes(inputSetCode));
        
        return (
          card.name.toLowerCase() === name.toLowerCase() &&
          (
            card.number === cardNumber ||
            card.number?.endsWith(cardNumber)
          ) &&
          setMatches
        );
      });
    }

    // Second pass with looser set code matching if needed
    if (!matchCard && results?.data?.length > 0) {
      // First try to find a card that matches name and number, with flexible set code matching
      matchCard = results.data.find(card => {
        const cardSetPtcgoCode = card.set?.ptcgoCode?.toUpperCase() || '';
        const cardSetId = card.set?.id?.toUpperCase() || '';
        const inputSetCode = setCode.toUpperCase();
        
        // Check if the first 2-3 characters of set code match
        const partialSetMatch = 
          (inputSetCode.length >= 2 && (
            cardSetPtcgoCode.startsWith(inputSetCode.substring(0, 2)) ||
            cardSetId.startsWith(inputSetCode.substring(0, 2)) ||
            cardSetPtcgoCode.includes(inputSetCode) ||
            cardSetId.includes(inputSetCode)
          ));
        
        return (
          card.name.toLowerCase() === name.toLowerCase() &&
          (
            card.number === cardNumber ||
            (card.number && card.number.endsWith(cardNumber))
          ) &&
          partialSetMatch
        );
      });
      
      // Only as a last resort, match without considering set code
      if (!matchCard) {
        console.warn(`Warning: Matching ${name} ${cardNumber} without set code verification`);
        matchCard = results.data.find(card => {
          return (
            card.name.toLowerCase() === name.toLowerCase() &&
            (
              card.number === cardNumber ||
              (card.number && card.number.endsWith(cardNumber))
            )
          );
        });
      }
    }

    // Fallback: loose name match if exact doesn't work
    if (!matchCard && results?.data?.length > 0) {
      matchCard = results.data.find(card =>
        card.name.toLowerCase().includes(name.toLowerCase())
      );
    }

    // Super fallback for known problematic cards
    if (!matchCard) {
      // If this is one of our known problematic cards, try a final alternate approach
      if (name === "Boss's Orders" && setCode === "PR-SW") {
        try {
          // Very loose query just by name
          const fallbackResults = await searchCardsAPI({ q: `name:"Boss's Orders"` });
          // Take the first one if available
          if (fallbackResults?.data?.length > 0) {
            matchCard = fallbackResults.data[0];
          }
        } catch(e) {
          console.error("Fallback search failed:", e);
        }
      } 
      else if (name === "Ancient Booster Capsule" && setCode === "PAR") {
        try {
          // Very loose query just by name
          const fallbackResults = await searchCardsAPI({ q: `name:"Ancient Booster"` });
          // Take the first one if available
          if (fallbackResults?.data?.length > 0) {
            matchCard = fallbackResults.data[0];
          }
        } catch(e) {
          console.error("Fallback search failed:", e);
        }
      }
    }

    if (!matchCard) {
      console.warn(`No exact match for: ${name} ${setCode} ${cardNumber}`);
      // Log the first few results to help debug
      if (results?.data?.length > 0) {
        console.log('First few search results:', results.data.slice(0, 3).map(c => ({
          name: c.name,
          set: c.set?.id || c.set?.ptcgoCode,
          number: c.number
        })));
      }
      failedImports.push(`${quantity}x ${name} ${setCode} ${cardNumber} (Card not found)`);
      continue;
    } else {
      console.log(`Matched card: ${matchCard.name} from set ${matchCard.set?.id || matchCard.set?.ptcgoCode} #${matchCard.number}`);
    }

    matchCard.resolvedSetCode =
      matchCard.set?.ptcgoCode?.toUpperCase() ||
      matchCard.set?.id?.toUpperCase() ||
      '???';

    // Add a default supertype based on section if none exists
    if (!matchCard.supertype) {
      if (section === 'pokemon') {
        matchCard.supertype = 'Pokémon';
      } else if (section === 'trainers') {
        matchCard.supertype = 'Trainer';
      } else if (section === 'energy') {
        matchCard.supertype = 'Energy';
      }
    }

    const card = {
      ...matchCard,
      id: matchCard.id,
      name: matchCard.name,
      supertype: matchCard.supertype, // Ensure supertype is included
      set: matchCard.set, // ✅ This is what enables export to get set info
      resolvedSetCode:
        matchCard.set?.ptcgoCode?.toUpperCase() ||
        matchCard.set?.id?.toUpperCase() ||
        '???',
      imageUrl: matchCard.images?.small || matchCard.imageUrl,
      images: matchCard.images || {
        small: matchCard.imageUrl,
        large: matchCard.imageUrl
      },
      count: quantity,
      quantity,
      section: section, // Store the section this card was in
      _timestamp: Date.now() + Math.random()
    };

    const type = card.supertype?.toLowerCase() || '';
    
    // Log for debugging
    console.log("Card type detection:", name, type, section);
    
    // Use section to determine card type if supertype is missing or not useful
    if (type === 'pokemon' || type === 'pokémon' || section === 'pokemon') {
      pokemonCards.push(card);
      console.log("Added to pokemonCards:", name);
    } else if (type === 'trainer' || section === 'trainers') {
      trainerCards.push(card);
      console.log("Added to trainerCards:", name);
    } else if (type === 'energy' || section === 'energy') {
      energyCards.push(card);
      console.log("Added to energyCards:", name);
    } else {
      // Default to pokemon if we can't determine section or type
      pokemonCards.push(card);
      console.log("Added to pokemonCards (default fallback):", name);
    }
  }

  return {
    name: deckName,
    pokemon: pokemonCards,
    trainers: trainerCards,
    energy: energyCards,
    failedImports: failedImports
  };
};